import LazyVideo from "@/components/LazyVideo";
import Link from "@/components/LocaleLink";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import captureDemo from "@/assets/capture-demo.mp4";
import RiskManagementMockup from "@/components/RiskManagementMockup";
import { AIAssistantHeroMockup } from "@/components/AIAssistantMockups";
import trackDemo from "@/assets/track-demo.mp4";
import automateDemo from "@/assets/automate-demo.mp4";
import {
  MessageSquare,
  Mail,
  Brain,
  Mic,
  CheckSquare,
  BarChart3,
  Link as LinkIcon,
  LayoutDashboard,
  Clock,
  Search,
  AlertTriangle,
  FileText,
  Download,
  Share2,
  Edit3,
  Receipt,
  CreditCard,
  Send,
  Bot,
  MessageCircle,
  BookOpen,
  ShieldCheck,
  Bell,
  FileCheck,
  ScanText,
  FileSearch,
  Sparkles,
  TrendingDown,
  Lightbulb,
  CalendarClock,
  GitBranch,
  Timer,
  Calculator,
} from "lucide-react";

interface AllFeaturesSectionProps {
  /** Override the small badge above the heading */
  label?: string;
  /** Override first line of the heading */
  titleLine1?: string;
  /** Override second (coloured) line of the heading */
  titleLine2?: string;
  /** Override the subtitle paragraph */
  subtitle?: string;
}

const AllFeaturesSection = ({ label, titleLine1, titleLine2, subtitle }: AllFeaturesSectionProps = {}) => {
  const { t } = useTranslation();

  const features = [
    {
      label: t('platformFeatures.f1Label'),
      title: t('platformFeatures.f1Title'),
      description: t('platformFeatures.f1Desc'),
      tags: [t('platformFeatures.f1Tag1'), t('platformFeatures.f1Tag2'), t('platformFeatures.f1Tag3'), t('platformFeatures.f1Tag4')],
      icons: [MessageSquare, Mail, Brain, Mic],
      video: captureDemo,
      href: "/construction-photo-documentation",
    },
    {
      label: t('platformFeatures.f2Label'),
      title: t('platformFeatures.f2Title'),
      description: t('platformFeatures.f2Desc'),
      tags: [t('platformFeatures.f2Tag1'), t('platformFeatures.f2Tag2'), t('platformFeatures.f2Tag3'), t('platformFeatures.f2Tag4')],
      icons: [CheckSquare, BarChart3, LinkIcon, CalendarClock],
      video: trackDemo,
      href: "/construction-progress-tracking",
    },
    {
      label: t('platformFeatures.f3Label'),
      title: t('platformFeatures.f3Title'),
      description: t('platformFeatures.f3Desc'),
      tags: [t('platformFeatures.f3Tag1'), t('platformFeatures.f3Tag2'), t('platformFeatures.f3Tag3'), t('platformFeatures.f3Tag4')],
      icons: [FileText, Download, Share2, Edit3],
      video: automateDemo,
      href: "/construction-reports",
    },
    {
      label: t('platformFeatures.f4Label'),
      title: t('platformFeatures.f4Title'),
      description: t('platformFeatures.f4Desc'),
      tags: [t('platformFeatures.f4Tag1'), t('platformFeatures.f4Tag2'), t('platformFeatures.f4Tag3'), t('platformFeatures.f4Tag4')],
      icons: [AlertTriangle, TrendingDown, Lightbulb, FileText],
      mockup: <RiskManagementMockup />,
      href: "/construction-risk-management",
    },
    {
      label: t('platformFeatures.f5Label'),
      title: t('platformFeatures.f5Title'),
      description: t('platformFeatures.f5Desc'),
      tags: [t('platformFeatures.f5Tag1'), t('platformFeatures.f5Tag2'), t('platformFeatures.f5Tag3'), t('platformFeatures.f5Tag4'), t('platformFeatures.f5Tag5'), t('platformFeatures.f5Tag6')],
      icons: [FileText, Mic, Search, Send, Bot, MessageCircle],
      mockup: <AIAssistantHeroMockup />,
      href: "/ai-for-construction",
    },
  ];

  const compactFeatures = [
    { label: t('platformFeatures.c1Label'), title: t('platformFeatures.c1Title'), description: t('platformFeatures.c1Desc'), icon: Receipt, href: "/contractor-invoicing" },
    { label: t('platformFeatures.c2Label'), title: t('platformFeatures.c2Title'), description: t('platformFeatures.c2Desc'), icon: ShieldCheck, href: "/construction-compliance" },
    { label: t('platformFeatures.c3Label'), title: t('platformFeatures.c3Title'), description: t('platformFeatures.c3Desc'), icon: MessageSquare, href: "/construction-daily-log" },
    { label: t('platformFeatures.c4Label'), title: t('platformFeatures.c4Title'), description: t('platformFeatures.c4Desc'), icon: CalendarClock, href: "/construction-scheduling" },
  ];

  const compactFeaturesRow2 = [
    { label: t('platformFeatures.c5Label'), title: t('platformFeatures.c5Title'), description: t('platformFeatures.c5Desc'), icon: ScanText, href: "/construction-document-management" },
    { label: t('platformFeatures.c6Label'), title: t('platformFeatures.c6Title'), description: t('platformFeatures.c6Desc'), icon: Share2, href: "/share-updates" },
    { label: t('platformFeatures.c7Label'), title: t('platformFeatures.c7Title'), description: t('platformFeatures.c7Desc'), icon: Search, href: "/punch-list-software" },
    { label: t('platformFeatures.c8Label'), title: t('platformFeatures.c8Title'), description: t('platformFeatures.c8Desc'), icon: Calculator, href: "/ai-construction-estimating" },
  ];

  return (
    <section className="bg-background py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-14 pb-10 lg:pb-12 border-b border-border/60">
          <p className="text-primary text-sm font-semibold uppercase tracking-wider mb-4">
            {label ?? t('platformFeatures.label')}
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground leading-tight max-w-4xl mx-auto"
            style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}
          >
            {titleLine1 ?? t('platformFeatures.title')}<br />
            {titleLine2 ?? t('platformFeatures.titleLine2')}
          </h2>
          <p className="text-muted-foreground text-lg lg:text-xl mt-4 max-w-3xl mx-auto leading-relaxed">
            {subtitle ?? t('platformFeatures.subtitle')}
          </p>
        </div>

        {/* Main Feature Cards */}
        <div className="space-y-0">
          {features.map((feature, index) => (
            <div
              key={feature.label}
              className="grid md:grid-cols-2 gap-6 lg:gap-12 items-center py-6 lg:py-8 border-b border-border/60 last:border-b-0"
            >
              <div className={`space-y-3 ${index % 2 === 1 ? "md:order-2" : ""}`}>
                <p className="text-primary text-xs font-semibold uppercase tracking-wider">{feature.label}</p>
                <h3 className="text-xl lg:text-2xl font-bold text-foreground leading-tight">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm lg:text-base max-w-md">
                  {feature.description}
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {feature.tags.map((tag, i) => {
                    const Icon = feature.icons[i];
                    return (
                      <span key={tag} className="inline-flex items-center gap-1.5 bg-foreground/5 border border-foreground/10 text-foreground/70 text-xs font-medium px-3 py-1.5 rounded-full">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {tag}
                      </span>
                    );
                  })}
                </div>
                <Link to={feature.href} className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold pt-2 hover:underline group/link rtl:flex-row-reverse">
                  {t('platformFeatures.learnMore')}
                  <span className="transition-transform duration-200 group-hover/link:translate-x-1">→</span>
                </Link>
              </div>

              <div className={`${index % 2 === 1 ? "md:order-1" : ""}`}>
                {feature.video ? (
                  <div className="rounded-2xl overflow-hidden">
                    <LazyVideo src={feature.video} className="w-full" style={{ marginBottom: '-4px' }} />
                  </div>
                ) : (
                  feature.mockup ? (
                    <div className="flex items-center justify-center">
                      {feature.mockup}
                    </div>
                  ) : (
                  <div className="bg-card rounded-2xl flex items-center justify-center min-h-[280px]">
                    {(() => { const Icon = feature.icons[0]; return <Icon className="w-12 h-12 text-muted-foreground/30" />; })()}
                  </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Compact Feature Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-8">
          {compactFeatures.map((feature) => {
            const Icon = feature.icon;
            return (
              <Link to={feature.href} key={feature.label} className="relative bg-card border border-border rounded-2xl p-6 space-y-3 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1 hover:border-primary/30 group">
                <ExternalLink className="absolute top-4 right-4 w-4 h-4 text-muted-foreground/50 md:hidden" />
                <Icon className="w-8 h-8 text-primary transition-transform duration-300 group-hover:scale-110" />
                <p className="text-primary text-xs font-semibold uppercase tracking-wider">{feature.label}</p>
                <h3 className="text-base lg:text-lg font-bold text-foreground leading-snug text-balance">{feature.title}</h3>
                <p className="hidden md:block text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
              </Link>
            );
          })}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-4">
          {compactFeaturesRow2.map((feature) => {
            const Icon = feature.icon;
            return (
              <Link to={feature.href} key={feature.label} className="relative bg-card border border-border rounded-2xl p-6 space-y-3 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1 hover:border-primary/30 group">
                <ExternalLink className="absolute top-4 right-4 w-4 h-4 text-muted-foreground/50 md:hidden" />
                <Icon className="w-8 h-8 text-primary transition-transform duration-300 group-hover:scale-110" />
                <p className="text-primary text-xs font-semibold uppercase tracking-wider">{feature.label}</p>
                <h3 className="text-base lg:text-lg font-bold text-foreground leading-snug text-balance">{feature.title}</h3>
                <p className="hidden md:block text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AllFeaturesSection;
